import { useCallback, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useToast } from "@/components/ui/toast";
import type { TimelineItem } from "@/lib/hooks/api/timeline/enhanced-timeline";

interface UseTimelineNavigationProps {
  timeline: TimelineItem[] | undefined;
  onItemSelect: (item: TimelineItem | null) => void;
  onModalOpen: (open: boolean) => void;
}

export const useTimelineNavigation = ({
  timeline,
  onItemSelect,
  onModalOpen,
}: UseTimelineNavigationProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  // Handle URL parameters for deep linking
  useEffect(() => {
    if (!searchParams) return;

    const itemId = searchParams.get("item");
    if (itemId && timeline && timeline.length > 0) {
      const item = timeline.find((t) => t.id === itemId || t.external_id === itemId);
      if (item) {
        onItemSelect(item);
        onModalOpen(true);
      } else {
        // Item not found, show a toast and remove the parameter
        toast({
          type: "error",
          title: "Item Not Found",
          description: "The requested timeline item could not be found.",
        });
        const params = new URLSearchParams(searchParams.toString());
        params.delete("item");
        const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
        router.push(newUrl, { scroll: false });
      }
    }
  }, [searchParams, timeline, toast, router, onItemSelect, onModalOpen]);

  const handleTimelineItemClick = useCallback(
    (item: TimelineItem) => {
      // Open the detail modal for the clicked item
      onItemSelect(item);
      onModalOpen(true);

      // Update URL with item ID for deep linking
      if (searchParams) {
        const params = new URLSearchParams(searchParams.toString());
        params.set("item", item.external_id || item.id);
        router.push(`?${params.toString()}`, { scroll: false });
      }
    },
    [searchParams, router, onItemSelect, onModalOpen]
  );

  const handleModalClose = useCallback(
    (open: boolean) => {
      onModalOpen(open);

      if (!open && searchParams) {
        // Remove item parameter from URL when modal closes
        const params = new URLSearchParams(searchParams.toString());
        params.delete("item");
        const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
        router.push(newUrl, { scroll: false });
        onItemSelect(null);
      }
    },
    [searchParams, router, onItemSelect, onModalOpen]
  );

  const handleCopyItemLink = useCallback(
    async (item: TimelineItem) => {
      // Build a shareable link to the current page with the item parameter
      const url = new URL(window.location.href);
      url.searchParams.set("item", item.external_id || item.id);
      const linkUrl = url.toString();

      try {
        await navigator.clipboard.writeText(linkUrl);
        toast({
          type: "success",
          title: "Link Copied",
          description: "Timeline item link has been copied to clipboard.",
        });
      } catch (error) {
        toast({
          type: "error",
          title: "Copy Failed",
          description: "Failed to copy link. Please try again.",
        });
      }
    },
    [toast]
  );

  return {
    handleTimelineItemClick,
    handleModalClose,
    handleCopyItemLink,
  };
};
